import { useEffect, useState } from "react";
import Marquee from "react-fast-marquee";
import axios from "axios";
import { TrendingUp, TrendingDown } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FALLBACK = [
  { symbol: "BTC", price: 102345.12, change24h: 1.85 },
  { symbol: "ETH", price: 3450.21, change24h: -0.42 },
  { symbol: "XRP", price: 2.41, change24h: 3.12 },
  { symbol: "SOL", price: 215.66, change24h: 2.05 },
  { symbol: "SUI", price: 4.31, change24h: -1.21 },
  { symbol: "DOGE", price: 0.382, change24h: 4.51 },
  { symbol: "HYPE", price: 33.27, change24h: 5.92 },
];

const formatPrice = (p) => {
  if (p === null || p === undefined) return "--";
  if (p >= 1000) return `$${p.toLocaleString("en-US", { maximumFractionDigits: 0 })}`;
  if (p >= 1) return `$${p.toLocaleString("en-US", { maximumFractionDigits: 2 })}`;
  return `$${p.toLocaleString("en-US", { maximumFractionDigits: 4 })}`;
};

const CoinPill = ({ coin }) => {
  const positive = (coin.change24h ?? 0) >= 0;
  return (
    <div
      data-testid={`ticker-coin-${coin.symbol}`}
      className="inline-flex items-center gap-3 mx-2 md:mx-3 px-4 md:px-5 h-12 md:h-14 rounded-full border border-white/10 bg-white/[0.03] backdrop-blur-md"
    >
      <span className="font-heading font-bold text-white text-sm md:text-base">
        {coin.symbol}
      </span>
      <span className="font-mono font-semibold text-white text-sm md:text-base">
        {formatPrice(coin.price)}
      </span>
      <span
        className={`inline-flex items-center gap-1 font-semibold text-xs md:text-sm ${
          positive ? "text-[var(--brand)]" : "text-[#FF3366]"
        }`}
      >
        {positive ? (
          <TrendingUp className="h-3.5 w-3.5" aria-hidden="true" />
        ) : (
          <TrendingDown className="h-3.5 w-3.5" aria-hidden="true" />
        )}
        {positive ? "+" : ""}
        {(coin.change24h ?? 0).toFixed(2)}%
      </span>
    </div>
  );
};

const CryptoTicker = () => {
  const { t } = useLanguage();
  const [coins, setCoins] = useState(FALLBACK);

  useEffect(() => {
    let mounted = true;
    const fetchPrices = async () => {
      try {
        const res = await axios.get(`${API}/prices`, { timeout: 9000 });
        if (mounted && res?.data?.coins?.length) {
          setCoins(res.data.coins);
        }
      } catch (e) {
        // keep fallback silently
      }
    };
    fetchPrices();
    const id = setInterval(fetchPrices, 30000);
    return () => {
      mounted = false;
      clearInterval(id);
    };
  }, []);

  return (
    <section
      id="prices"
      aria-labelledby="prices-heading"
      data-testid="crypto-ticker-section"
      className="relative border-y border-white/10 bg-white/[0.02] py-5 md:py-6 overflow-hidden"
    >
      <h2 id="prices-heading" className="sr-only">
        {t.a11y.prices_region}
      </h2>

      <div
        className="absolute left-0 top-0 bottom-0 w-16 md:w-24 bg-gradient-to-r from-[#050505] to-transparent z-10 pointer-events-none"
        aria-hidden="true"
      />
      <div
        className="absolute right-0 top-0 bottom-0 w-16 md:w-24 bg-gradient-to-l from-[#050505] to-transparent z-10 pointer-events-none"
        aria-hidden="true"
      />

      <div className="flex items-center mb-3 px-5 md:px-8 max-w-7xl mx-auto">
        <p className="flex items-center gap-2">
          <span className="relative flex h-2.5 w-2.5" aria-hidden="true">
            <span className="absolute inline-flex h-full w-full rounded-full bg-[var(--brand)] opacity-70 animate-ping" />
            <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-[var(--brand)]" />
          </span>
          <span className="text-xs md:text-sm font-semibold uppercase tracking-[0.2em] text-zinc-200">
            {t.ticker.title} · {t.ticker.subtitle}
          </span>
        </p>
      </div>

      {/* Visual marquee is decorative for assistive tech; an accessible
          live list of the same prices is provided below for screen readers. */}
      <div aria-hidden="true">
        <Marquee speed={45} gradient={false} pauseOnHover>
          {coins.map((coin) => (
            <CoinPill key={coin.symbol} coin={coin} />
          ))}
        </Marquee>
      </div>

      <ul
        className="sr-only"
        aria-live="polite"
        aria-label={t.a11y.prices_table}
        data-testid="crypto-prices-sr-list"
      >
        {coins.map((coin) => (
          <li key={coin.symbol}>
            {coin.symbol}: {formatPrice(coin.price)},{" "}
            {(coin.change24h ?? 0) >= 0 ? "+" : ""}
            {(coin.change24h ?? 0).toFixed(2)}% (24h)
          </li>
        ))}
      </ul>
    </section>
  );
};

export default CryptoTicker;
